/**
 * Transaction / Job detail page — same tabs and table style as Ignite DRFeedbackDialog.
 * Route: /log-oracle/transaction-lookup/:transactionId
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogContentText,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  CircularProgress,
  alpha,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TaskAlt as TaskAltIcon,
  DoNotDisturbOn as DoNotDisturbOnIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  DownloadOutlined as DownloadOutlinedIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { DataGridPremium, GridActionsCellItem, GridColDef, GridRowParams } from '@mui/x-data-grid-premium';
import dayjs from 'dayjs';
import JSZip from 'jszip';
import { useAuthentication } from '@/common-hooks/use-authentication';
import { PERMISSIONS } from '@/common-constants/permissions';
import { transactionService, jobService } from '@/services/api';
import type { TransactionDetail, JobDetailsResponse } from '@/common-types';
import { TAB_INDEX, SYNC_SUMMARY_STEPS, STEP_LABELS } from './transaction-detail-constants';


const JOB_DETAILS_SUPPORTED_REGIONS: string[] = ['us-west-2', 'eu-central-1'];
function resolveJobDetailsRegion(transactionRegion: string | undefined): string {
  const r = (transactionRegion ?? '').trim().toLowerCase();
  if (JOB_DETAILS_SUPPORTED_REGIONS.includes(r)) return r;
  return 'us-west-2';
}

function getOrgDisplay(t: TransactionDetail | null | undefined, key: 'sourceOrg' | 'destinationOrg'): { region?: string } | null {
  if (t == null) return null;
  const org = (t.exportOrgs as Record<string, { region?: string }> | undefined)?.[key];
  return org ? { region: org.region } : null;
}
function getSuccessfulSteps(t: TransactionDetail): string[] {
  return Array.isArray(t.successfullSteps ?? t.successfulSteps) ? (t.successfullSteps ?? t.successfulSteps)! : [];
}
function getFailedSteps(t: TransactionDetail): string[] {
  return Array.isArray(t.failedSteps) ? t.failedSteps : [];
}

function downloadUrlAsFile(url: string, fileName: string): Promise<void> {
  return fetch(url)
    .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(`Download failed (${r.status})`))))
    .then((blob) => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(a.href);
    })
    .catch((err) => {
      window.open(url, '_blank', 'noopener,noreferrer');
      return Promise.reject(err);
    });
}

/** Same shape as Ignite FileObject for SyncArchyErrorTable */
interface ArchyRow {
  id: string;
  flow: string;
  file: string;
  type: string;
  status: string;
  warningCount: number;
  errorCount: number;
  warnings: string[];
  errors: string[];
}

interface ResponseFilesJson {
  settings?: Record<string, { value: string; source: string }>;
  output?: { traces?: Array<{ type: string; text: string }> };
}

const EXPORT_FILTER_PHRASES = ['ERROR!', 'WARNING!', 'unable to find', 'is now deprecated'];
const PUBLISH_FILTER_PHRASES = [...EXPORT_FILTER_PHRASES, 'TTS'];

async function fetchZipAsBlob(url: string): Promise<Blob> {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`${r.status}`);
  return r.blob();
}

async function parseOneZipAsync(
  blob: Blob,
  status: 'succeeded' | 'failed',
  fileNameSuffix: string
): Promise<{ resultInfo: Array<{ name: string; json: ResponseFilesJson }>; fileObjects: Omit<ArchyRow, 'warnings' | 'errors' | 'warningCount' | 'errorCount'>[] }> {
  const jsZip = new JSZip();
  const z = await jsZip.loadAsync(blob);
  const resultInfo: Array<{ name: string; json: ResponseFilesJson }> = [];
  const fileObjects: Omit<ArchyRow, 'warnings' | 'errors' | 'warningCount' | 'errorCount'>[] = [];
  for (const zFile of Object.values(z.files)) {
    if (zFile.dir || !zFile.name.includes('.json')) continue;
    const text = await zFile.async('string');
    const json = JSON.parse(text) as ResponseFilesJson;
    const fileSplit = zFile.name.split('/');
    const fileName = fileSplit?.length > 1 ? fileSplit[1] : '';
    const fileNameSplit = fileName ? fileName.split(fileNameSuffix) : [];
    const flowName = fileNameSplit?.length > 0 ? fileNameSplit[0] : '';
    const flowType = fileSplit?.length > 0 ? fileSplit[0] : '';
    resultInfo.push({ name: fileName, json });
    fileObjects.push({
      id: `archy-${status}-${fileName}-${Math.random().toString(36).slice(2)}`,
      flow: flowName,
      file: fileName,
      type: flowType,
      status,
    });
  }
  return { resultInfo, fileObjects };
}

async function loadArchyRowsFromUrls(
  urls: { success?: string | null; errors?: string | null },
  kind: 'export' | 'publish'
): Promise<ArchyRow[]> {
  const suffix = kind === 'export' ? '-export-result' : '-publish';
  const filterPhrases = kind === 'export' ? EXPORT_FILTER_PHRASES : PUBLISH_FILTER_PHRASES;
  const resultInfoAll: Array<{ name: string; json: ResponseFilesJson }> = [];
  const fileObjectsAll: Omit<ArchyRow, 'warnings' | 'errors' | 'warningCount' | 'errorCount'>[] = [];

  if (urls.errors) {
    const blob = await fetchZipAsBlob(urls.errors);
    const { resultInfo, fileObjects } = await parseOneZipAsync(blob, 'failed', suffix);
    resultInfoAll.push(...resultInfo);
    fileObjectsAll.push(...fileObjects);
  }
  if (urls.success) {
    const blob = await fetchZipAsBlob(urls.success);
    const { resultInfo, fileObjects } = await parseOneZipAsync(blob, 'succeeded', suffix);
    resultInfoAll.push(...resultInfo);
    fileObjectsAll.push(...fileObjects);
  }

  return fileObjectsAll.map((fo) => {
    const row: ArchyRow = { ...fo, warnings: [], errors: [], warningCount: 0, errorCount: 0 };
    const match = resultInfoAll.find((r) => r.name === fo.file);
    if (match?.json) {
      const filePath = match.json.settings?.filePath?.value ?? '';
      if (filePath.includes(fo.type)) {
        const traces = match.json.output?.traces ?? [];
        row.warnings = [...new Set(traces.filter((t) => t.type === 'warning' && filterPhrases.some((s) => t.text.includes(s))).map((m) => m.text))];
        row.errors = [...new Set(traces.filter((t) => t.type === 'error' && filterPhrases.some((s) => t.text.includes(s))).map((m) => m.text))];
        row.warningCount = row.warnings.length;
        row.errorCount = row.errors.length;
      }
    }
    return row;
  });
}

export default function TransactionDetailPage() {
  const { transactionId: txnIdParam } = useParams<{ transactionId: string }>();
  const transactionId = txnIdParam ?? '';
  const { hasPermission } = useAuthentication();
  const canAccessLogOracle = hasPermission(PERMISSIONS.TRANSACTIONS_IN_LOG_ORACLE);
  const canViewJobDetails = hasPermission(PERMISSIONS.JOBS_IN_LOG_ORACLE);

  const [data, setData] = useState<TransactionDetail | null>(null);
  const [jobDetailsData, setJobDetailsData] = useState<JobDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [jobDetailsError, setJobDetailsError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [terraformDownloadError, setTerraformDownloadError] = useState<string | null>(null);
  const [terraformListError, setTerraformListError] = useState<string | null>(null);
  const [terraformRows, setTerraformRows] = useState<Array<{ id: string; '@message'?: string; type?: string; '@module'?: string; stampDisplayWithTime?: string }>>([]);
  const [terraformListLoading, setTerraformListLoading] = useState(false);
  const [exportArchyRows, setExportArchyRows] = useState<ArchyRow[]>([]);
  const [publishedArchyRows, setPublishedArchyRows] = useState<ArchyRow[]>([]);
  const [exportArchyLoading, setExportArchyLoading] = useState(false);
  const [publishedArchyLoading, setPublishedArchyLoading] = useState(false);
  const [archyDetailOpen, setArchyDetailOpen] = useState(false);
  const [archyDetailTitle, setArchyDetailTitle] = useState('');
  const [archyDetailContent, setArchyDetailContent] = useState<string[]>([]);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const terraformUrlRefreshAttemptedRef = useRef<Set<string>>(new Set());

  const rawRegion = useMemo(() => {
    if (!data) return undefined;
    return data.deploymentRegion ?? getOrgDisplay(data, 'sourceOrg')?.region ?? getOrgDisplay(data, 'destinationOrg')?.region;
  }, [data]);
  const region = useMemo(() => resolveJobDetailsRegion(rawRegion), [rawRegion]);
  const refreshJobDetails = useCallback(async (txnId: string, reg: string, options?: { suppressError?: boolean }) => {
    if (!canViewJobDetails || !txnId.trim()) return null;
    try {
      const next = await jobService.getJobDetails(txnId, reg);
      setJobDetailsData(next);
      return next;
    } catch (err) {
      if (!options?.suppressError) {
        setJobDetailsError(err instanceof Error ? err.message : 'Failed to load job details');
      }
      return null;
    }
  }, [canViewJobDetails]);

  useEffect(() => {
    if (!transactionId.trim() || !canAccessLogOracle) return;
    let cancelled = false;
    queueMicrotask(() => { if (!cancelled) setLoading(true); });
    transactionService
      .getTransactionById(transactionId)
      .then((txn) => {
        if (cancelled) return;
        setData(txn ?? null);
        if (!txn?.transactionId) {
          setLoading(false);
          return;
        }
        if (!canViewJobDetails) {
          setLoading(false);
          return;
        }
        const reg = resolveJobDetailsRegion(txn.deploymentRegion ?? getOrgDisplay(txn, 'sourceOrg')?.region ?? getOrgDisplay(txn, 'destinationOrg')?.region);
        return jobService.getJobDetails(txn.transactionId!, reg).then((jobResp) => {
          if (!cancelled) {
            setJobDetailsData(jobResp);
            setExportArchyRows([]);
            setPublishedArchyRows([]);
            setJobDetailsError(null);
          }
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load transaction');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [transactionId, canAccessLogOracle, canViewJobDetails]);

  useEffect(() => {
    const isProcessing = jobDetailsData?.terraformErrors?.status === 'processing';
    if (!transactionId || !canViewJobDetails || !jobDetailsData || !isProcessing) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }
    const id = setInterval(() => {
      refreshJobDetails(transactionId, region, { suppressError: true }).then((next) => {
        if (next?.terraformErrors?.status !== 'processing' && pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      });
    }, 5000);
    pollingRef.current = id;
    return () => {
      clearInterval(id);
      pollingRef.current = null;
    };
  }, [transactionId, region, canViewJobDetails, jobDetailsData, refreshJobDetails]);

  useEffect(() => {
    const url = jobDetailsData?.terraformErrors?.presignedUrl;
    if (tab !== TAB_INDEX.TERRAFORM_ERRORS || !url || jobDetailsData?.terraformErrors?.status !== 'ready') return;
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      setTerraformListError(null);
      setTerraformListLoading(true);
    });
    fetch(url)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`${r.status}`))))
      .then((text) => {
        if (cancelled) return;
        const json = JSON.parse(text) as { errors?: Array<Record<string, unknown>>; processingError?: string };
        if (json.processingError) {
          setTerraformRows([]);
          setTerraformListError(json.processingError);
          return;
        }
        const rows = (json.errors ?? []).map((e, i) => ({
          id: `row-${i}`,
          '@message': (e['@message'] as string) ?? '—',
          type: (e['@level'] as string) ?? (e['type'] as string),
          '@module': (e['@module'] as string) ?? '—',
          stampDisplayWithTime: e['@timestamp'] ? dayjs(e['@timestamp'] as string).format('YYYY-MM-DD HH:mm:ss') : '—',
        }));
        setTerraformRows(rows);
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Unable to load Terraform errors';
        setTerraformListError(`Terraform errors could not be loaded (${message}).`);
        if (!terraformUrlRefreshAttemptedRef.current.has(url)) {
          terraformUrlRefreshAttemptedRef.current.add(url);
          refreshJobDetails(transactionId, region, { suppressError: true });
        }
      })
      .finally(() => { if (!cancelled) setTerraformListLoading(false); });
    return () => { cancelled = true; };
  }, [tab, jobDetailsData, refreshJobDetails, transactionId, region]);

  useEffect(() => {
    const urls = jobDetailsData?.presignedUrls?.exportedFlows;
    if ((!urls?.success && !urls?.errors) || exportArchyRows.length > 0) return;
    let cancelled = false;
    queueMicrotask(() => { if (!cancelled) setExportArchyLoading(true); });
    loadArchyRowsFromUrls({ success: urls?.success ?? null, errors: urls?.errors ?? null }, 'export')
      .then((rows) => { if (!cancelled) setExportArchyRows(rows); })
      .catch(() => { if (!cancelled) setExportArchyRows([]); })
      .finally(() => { if (!cancelled) setExportArchyLoading(false); });
    return () => { cancelled = true; };
  }, [jobDetailsData?.presignedUrls?.exportedFlows, exportArchyRows.length]);

  useEffect(() => {
    const urls = jobDetailsData?.presignedUrls?.publishedFlows;
    if ((!urls?.success && !urls?.errors) || publishedArchyRows.length > 0) return;
    let cancelled = false;
    queueMicrotask(() => { if (!cancelled) setPublishedArchyLoading(true); });
    loadArchyRowsFromUrls({ success: urls?.success ?? null, errors: urls?.errors ?? null }, 'publish')
      .then((rows) => { if (!cancelled) setPublishedArchyRows(rows); })
      .catch(() => { if (!cancelled) setPublishedArchyRows([]); })
      .finally(() => { if (!cancelled) setPublishedArchyLoading(false); });
    return () => { cancelled = true; };
  }, [jobDetailsData?.presignedUrls?.publishedFlows, publishedArchyRows.length]);

  const handleDownload = useCallback((url: string, key: string, fileName: string, options?: { checkTerraformProcessingError?: boolean }) => {
    if (downloading) return;
    setDownloading(key);
    setTerraformDownloadError(null);
    const done = () => setDownloading(null);
    if (options?.checkTerraformProcessingError) {
      fetch(url)
        .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`${r.status}`))))
        .then((text) => {
          try {
            const json = JSON.parse(text) as { processingError?: string };
            if (json.processingError) setTerraformDownloadError(json.processingError);
          } catch { /* ignore */ }
          const blob = new Blob([text], { type: 'application/json' });
          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = fileName;
          a.click();
          URL.revokeObjectURL(a.href);
        })
        .catch(() => {})
        .finally(done);
    } else {
      downloadUrlAsFile(url, fileName).catch(() => {}).finally(done);
    }
  }, [downloading]);

  const successfulSteps = useMemo(() => (data ? getSuccessfulSteps(data) : []), [data]);
  const failedSteps = useMemo(() => (data ? getFailedSteps(data) : []), [data]);
  const jobCompletionPct = useMemo(() => {
    const c = data?.completionPercentage ?? data?.completion;
    return typeof c === 'number' ? c : Number(c) || 0;
  }, [data]);
  const syncStepStatuses = useMemo(() => {
    if (!data) return [];
    return SYNC_SUMMARY_STEPS.map(({ step, minPct }) => {
      const failed = failedSteps.some((s) => s.includes(step));
      const success = successfulSteps.some((s) => s.includes(step));
      if (failed && (minPct == null || jobCompletionPct < minPct)) return false;
      if (success && (minPct == null || jobCompletionPct >= minPct)) return true;
      return undefined;
    });
  }, [data, failedSteps, successfulSteps, jobCompletionPct]);
  const lastFailStep = useMemo(() => {
    const idx = syncStepStatuses.findIndex((s) => s === false);
    return idx >= 0 ? idx + 1 : 0;
  }, [syncStepStatuses]);
  const jobListedMessage = useMemo(() => {
    const err = data?.error ?? (data as { jobError?: { message?: string } })?.jobError?.message ?? (data as { jobError?: string })?.jobError;
    if (typeof err === 'string') return err;
    if (err && typeof (err as { message?: string }).message === 'string') return (err as { message: string }).message;
    return '';
  }, [data]);
  const unsupportedFlows = useMemo(() => {
    const result = (data?.createFlowsFromBlankTemplates as { result?: { flowsUnsupported?: Array<{ type?: string; name?: string }> } })?.result?.flowsUnsupported;
    return result ?? [];
  }, [data]);

  const terraformColumns: GridColDef[] = useMemo(() => [
    { field: '@message', headerName: 'Message', flex: 1, minWidth: 400 },
    { field: 'type', headerName: 'Type', width: 120 },
    { field: '@module', headerName: 'Module', width: 160 },
    { field: 'stampDisplayWithTime', headerName: 'Timestamp', width: 180 },
  ], []);

  const openArchyDetail = useCallback((title: string, items: string[]) => {
    setArchyDetailTitle(title);
    setArchyDetailContent(items);
    setArchyDetailOpen(true);
  }, []);

  const archyColumns: GridColDef<ArchyRow>[] = useMemo(() => [
    { field: 'flow', headerName: 'Flow', type: 'string', width: 400, hideable: false },
    { field: 'type', headerName: 'Flow Type', type: 'string', width: 200, hideable: true },
    { field: 'status', headerName: 'Status', type: 'string', width: 100, hideable: true },
    { field: 'warningCount', headerName: '# of Warnings', type: 'number', width: 100, hideable: true },
    { field: 'errorCount', headerName: '# of Errors', type: 'number', width: 100, hideable: true },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      width: 200,
      hideable: false,
      getActions: (params: GridRowParams<ArchyRow>) => {
        const row = params.row;
        const actions: ReactElement[] = [];
        if (row.warningCount > 0) {
          actions.push(
            <GridActionsCellItem
              key="warnings"
              icon={<Tooltip title="Architect Validation Warnings"><DescriptionOutlinedIcon color="warning" fontSize="small" /></Tooltip>}
              label="Architect Validation Warnings"
              onClick={() => openArchyDetail('Architect Validation Warnings', row.warnings)}
            />
          );
        }
        if (row.errorCount > 0) {
          actions.push(
            <GridActionsCellItem
              key="errors"
              icon={<Tooltip title="Architect Validation Errors"><DescriptionOutlinedIcon color="error" fontSize="small" /></Tooltip>}
              label="Architect Validation Errors"
              onClick={() => openArchyDetail('Architect Validation Errors', row.errors)}
            />
          );
        }
        return actions;
      },
    },
  ], [openArchyDetail]);

  if (!canAccessLogOracle) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">You need permission to view Log Oracle.</Alert>
      </Box>
    );
  }

  if (!transactionId.trim()) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">Missing transaction ID.</Alert>
        <Button component={Link} to="/log-oracle/transaction-lookup" startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>Back to Transaction Lookup</Button>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error ?? 'Transaction not found'}</Alert>
        <Button component={Link} to="/log-oracle/transaction-lookup" startIcon={<ArrowBackIcon />}>Back to Transaction Lookup</Button>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} disableGutters>
      {/* Header: back + title + transaction ID */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: 1, borderColor: 'divider' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} flexWrap="wrap" useFlexGap>
          <Button
            component={Link}
            to="/log-oracle/transaction-lookup"
            startIcon={<ArrowBackIcon />}
            size="medium"
            variant="outlined"
            sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' }, textTransform: 'none', fontWeight: 600 }}
          >
            Back to Transaction Lookup
          </Button>
          <Typography variant="h6" fontWeight={700} sx={{ flex: 1, minWidth: 0 }}>
            Disaster Recovery Sync Details
          </Typography>
          <Typography component="span" variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
            {data.transactionId}
          </Typography>
        </Stack>
      </Paper>

      {jobDetailsError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setJobDetailsError(null)}>{jobDetailsError}</Alert>
      )}

      <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden', mb: 2 }}>
        <Tabs value={tab} variant="scrollable" scrollButtons allowScrollButtonsMobile onChange={(_, v: number) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Sync Summary" />
          <Tab label="Terraform Errors" />
          <Tab label="Exported Flow Details" />
          <Tab label="Published Flow Details" />
          {unsupportedFlows.length > 0 && <Tab label="Unsupported Flows" />}
        </Tabs>

        {/* Sync Summary */}
        {tab === TAB_INDEX.SYNC_SUMMARY && (
          <Box sx={{ p: 2, minHeight: 'min(400px, calc(100vh - 16em))', maxHeight: 'calc(100vh - 16em)', overflow: 'hidden', overflowY: 'auto' }}>
        <Grid container rowSpacing={1} columnSpacing={{ xs: 2, md: 2 }}>
          <Grid size={{ xs: 12 }}>
            {SYNC_SUMMARY_STEPS.map(({ key, step }, i) => {
              const stepStatus = syncStepStatuses[i];
              const stepNum = i + 1;
              const isFailStep = lastFailStep === stepNum;
              const severity = stepStatus === true ? 'success' : stepStatus === false ? 'error' : 'info';
              const Icon = stepStatus === true ? TaskAltIcon : DoNotDisturbOnIcon;
              const label = step === 'importOrg' ? (stepNum === 6 ? 'Import Pass #1' : 'Final Import Pass') : STEP_LABELS[step];
              return (
                <Alert key={key} sx={{ mb: 0.5, pt: 0.2, pb: 0 }} variant="outlined" icon={<Icon fontSize="inherit" />} severity={severity}>
                  <AlertTitle>{stepNum}: {label}</AlertTitle>
                  {isFailStep && jobListedMessage && <Typography variant="body2" component="span" sx={{ whiteSpace: 'pre-wrap' }}>{jobListedMessage}</Typography>}
                  {stepNum === 9 && <Typography variant="body2" component="span">See the &quot;Terraform Errors&quot; tab for details on any errors encountered during this step.</Typography>}
                </Alert>
              );
            })}
            {lastFailStep === 0 && jobListedMessage && (
              <Alert sx={{ mb: 0.5 }} variant="outlined" icon={<DoNotDisturbOnIcon fontSize="inherit" />} severity="error">
                <AlertTitle>Other Error(s)</AlertTitle>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{jobListedMessage}</Typography>
              </Alert>
            )}
          </Grid>
        </Grid>
          </Box>
        )}

        {/* Terraform Errors */}
        {tab === TAB_INDEX.TERRAFORM_ERRORS && (
          <Box sx={{ p: 2, minHeight: 'min(400px, calc(100vh - 16em))', maxHeight: 'calc(100vh - 16em)', overflow: 'hidden', overflowY: 'auto' }}>
        <Grid container rowSpacing={1} columnSpacing={{ xs: 2, md: 2 }}>
        <Grid size={{ xs: 12 }}>
        <Box>
          {jobDetailsData?.terraformErrors?.status === 'processing' && (
            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">Extracting errors… (polling)</Typography>
            </Stack>
          )}
          {jobDetailsData?.terraformErrors?.status === 'ready' && jobDetailsData.terraformErrors.presignedUrl && (
            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={downloading === 'terraformErrors' ? <CircularProgress size={14} color="inherit" /> : <DownloadOutlinedIcon fontSize="small" />}
                disabled={!!downloading}
                onClick={() => handleDownload(jobDetailsData.terraformErrors.presignedUrl!, 'terraformErrors', 'terraformErrors.json', { checkTerraformProcessingError: true })}
              >
                Download errors
              </Button>
            </Stack>
          )}
          {(jobDetailsData?.terraformErrors?.status === 'unavailable' || jobDetailsData?.terraformErrors?.status === 'error') && jobDetailsData?.terraformErrors?.error && (
            <Typography variant="body2" color="error.main" sx={{ mb: 2 }}>{jobDetailsData.terraformErrors.error}</Typography>
          )}
          {terraformDownloadError && (
            <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setTerraformDownloadError(null)}>Processing error: {terraformDownloadError}</Alert>
          )}
          {terraformListError && (
            <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setTerraformListError(null)}>{terraformListError}</Alert>
          )}
          {terraformListLoading && <CircularProgress size={24} sx={{ my: 2 }} />}
          {!terraformListLoading && !terraformListError && terraformRows.length === 0 && jobDetailsData?.terraformErrors?.status === 'ready' && (
            <Paper elevation={0} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4, borderRadius: 2, bgcolor: (t) => alpha(t.palette.success.main, 0.08), border: '1px solid', borderColor: 'success.light', maxWidth: 320 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 48, color: 'success.main', mb: 1.5 }} />
              <Typography variant="subtitle1" fontWeight={500} color="success.dark">No Terraform Errors</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, textAlign: 'center' }}>The sync completed without any Terraform errors.</Typography>
            </Paper>
          )}
          {!terraformListLoading && terraformRows.length > 0 && (
            <Box sx={{ minHeight: 320, height: { xs: 360, sm: 480 }, width: '100%' }}>
              <DataGridPremium
                rows={terraformRows}
                columns={terraformColumns}
                getRowId={(row) => row.id}
                density="compact"
                pageSizeOptions={[25, 50, 100]}
                pagination
                disableRowSelectionOnClick
              />
            </Box>
          )}
        </Box>
        </Grid>
        </Grid>
          </Box>
        )}

        {/* Exported Flow Details */}
        {tab === TAB_INDEX.EXPORTED_FLOW_DETAILS && (
          <Box sx={{ p: 2, minHeight: 'min(400px, calc(100vh - 16em))', maxHeight: 'calc(100vh - 16em)', overflow: 'hidden', overflowY: 'auto' }}>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 2, md: 2 }}>
            <Grid size={{ xs: 12 }}>
              {exportArchyLoading && (
                <Stack direction="row" alignItems="center" gap={1} sx={{ py: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="text.secondary">Loading exported flow details…</Typography>
                </Stack>
              )}
              {!exportArchyLoading && exportArchyRows.length === 0 && !jobDetailsData?.presignedUrls?.exportedFlows?.success && !jobDetailsData?.presignedUrls?.exportedFlows?.errors && (
                <Typography variant="body2" color="text.secondary">No exported flow files available.</Typography>
              )}
              {!exportArchyLoading && exportArchyRows.length === 0 && (jobDetailsData?.presignedUrls?.exportedFlows?.success || jobDetailsData?.presignedUrls?.exportedFlows?.errors) && (
                <Typography variant="body2" color="text.secondary">No flow entries in the exported archives.</Typography>
              )}
              {!exportArchyLoading && exportArchyRows.length > 0 && (
                <Box sx={{ minHeight: 320, height: { xs: 360, sm: 480 }, width: '100%' }}>
                  <DataGridPremium
                    rows={exportArchyRows}
                    columns={archyColumns}
                    getRowId={(row) => row.id}
                    density="compact"
                    pageSizeOptions={[25, 50, 75, 100]}
                    pagination
                    disableRowSelectionOnClick
                    initialState={{ rowGrouping: { model: ['status', 'type'] } }}
                    groupingColDef={{ headerName: 'Grouping (Status, Flow Type)' }}
                  />
                </Box>
              )}
            </Grid>
          </Grid>
          </Box>
        )}

        {/* Published Flow Details */}
        {tab === TAB_INDEX.PUBLISHED_FLOW_DETAILS && (
          <Box sx={{ p: 2, minHeight: 'min(400px, calc(100vh - 16em))', maxHeight: 'calc(100vh - 16em)', overflow: 'hidden', overflowY: 'auto' }}>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 2, md: 2 }}>
            <Grid size={{ xs: 12 }}>
              <Alert severity="warning" sx={{ my: 1 }}>
                <AlertTitle>Disclaimer</AlertTitle>
                Make sure to address any unresolved errors in the &quot;Terraform Errors&quot; tab before delving into flow publishing issues. Although flow errors aren&apos;t always caused by missing dependencies from the previous step, it&apos;s a best practice to resolve them first. Remember, the flow publishing process operates independently from the configuration applied by the Terraform provider.
              </Alert>
              {publishedArchyLoading && (
                <Stack direction="row" alignItems="center" gap={1} sx={{ py: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="text.secondary">Loading published flow details…</Typography>
                </Stack>
              )}
              {!publishedArchyLoading && publishedArchyRows.length === 0 && !jobDetailsData?.presignedUrls?.publishedFlows?.success && !jobDetailsData?.presignedUrls?.publishedFlows?.errors && (
                <Typography variant="body2" color="text.secondary">No published flow files available.</Typography>
              )}
              {!publishedArchyLoading && publishedArchyRows.length === 0 && (jobDetailsData?.presignedUrls?.publishedFlows?.success || jobDetailsData?.presignedUrls?.publishedFlows?.errors) && (
                <Typography variant="body2" color="text.secondary">No flow entries in the published archives.</Typography>
              )}
              {!publishedArchyLoading && publishedArchyRows.length > 0 && (
                <Box sx={{ minHeight: 320, height: { xs: 360, sm: 480 }, width: '100%' }}>
                  <DataGridPremium
                    rows={publishedArchyRows}
                    columns={archyColumns}
                    getRowId={(row) => row.id}
                    density="compact"
                    pageSizeOptions={[25, 50, 75, 100]}
                    pagination
                    disableRowSelectionOnClick
                    initialState={{ rowGrouping: { model: ['status', 'type'] } }}
                    groupingColDef={{ headerName: 'Grouping (Status, Flow Type)' }}
                  />
                </Box>
              )}
            </Grid>
          </Grid>
          </Box>
        )}

        {/* Unsupported Flows — DataGrid table like other tabs */}
        {unsupportedFlows.length > 0 && tab === TAB_INDEX.UNSUPPORTED_FLOWS && (
          <Box sx={{ p: 2, minHeight: 'min(400px, calc(100vh - 16em))', maxHeight: 'calc(100vh - 16em)', overflow: 'hidden', overflowY: 'auto' }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Unsupported Flows</AlertTitle>
              Below are currently <strong>unsupported</strong> flows that were not published due to the corresponding flow type not being supported. (Step 7 of Sync Summary)
            </Alert>
            <Box sx={{ minHeight: 320, height: { xs: 360, sm: 480 }, width: '100%' }}>
              <DataGridPremium
                rows={unsupportedFlows.map((f, i) => ({ id: `unsupported-${i}`, type: f.type ?? '—', name: f.name ?? '—' }))}
                columns={[
                  { field: 'type', headerName: 'Flow Type', flex: 1, minWidth: 200 },
                  { field: 'name', headerName: 'Name', flex: 2, minWidth: 300 },
                ]}
                getRowId={(row) => row.id}
                density="compact"
                pageSizeOptions={[25, 50, 100]}
                pagination
                disableRowSelectionOnClick
              />
            </Box>
          </Box>
        )}
      </Paper>

      {/* Footer: downloads + back — responsive, always visible */}
      <Box
        sx={{
          position: { xs: 'fixed', sm: 'static' },
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap" useFlexGap>
          {jobDetailsData?.presignedUrls?.terraformConfig && (
            <Button
              variant="contained"
              color="primary"
              size="medium"
              startIcon={downloading === 'terraformConfig' ? <CircularProgress size={18} color="inherit" /> : <DownloadOutlinedIcon />}
              onClick={() => handleDownload(jobDetailsData!.presignedUrls!.terraformConfig!, 'terraformConfig', 'terraform-config.zip')}
              disabled={!!downloading}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {downloading === 'terraformConfig' ? 'Downloading…' : 'Download Configuration File'}
            </Button>
          )}
          {jobDetailsData?.presignedUrls?.terraformLogs && (
            <Button
              variant="contained"
              color="primary"
              size="medium"
              startIcon={downloading === 'terraformLogs' ? <CircularProgress size={18} color="inherit" /> : <DownloadOutlinedIcon />}
              onClick={() => handleDownload(jobDetailsData!.presignedUrls!.terraformLogs!, 'terraformLogs', 'terraform-logs.json')}
              disabled={!!downloading}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {downloading === 'terraformLogs' ? 'Downloading…' : 'Download Logs'}
            </Button>
          )}
        </Stack>
      </Box>

      {/* Dialog for Architect Validation Warnings/Errors */}
      <Dialog open={archyDetailOpen} onClose={() => setArchyDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogContent>
          <DialogContentText variant="subtitle2" component="div" sx={{ mb: 1 }}>{archyDetailTitle}</DialogContentText>
          <Box component="pre" sx={{ maxHeight: 'calc(100vh - 12em)', overflow: 'auto', bgcolor: 'action.hover', p: 1, borderRadius: 1, fontSize: '0.75rem' }}>
            {archyDetailContent.length === 0 ? '—' : archyDetailContent.map((line, i) => <Typography key={i} component="span" variant="body2" display="block">{line}</Typography>)}
          </Box>
          <Button size="medium" variant="outlined" color="secondary" startIcon={<CloseIcon />} onClick={() => setArchyDetailOpen(false)} sx={{ mt: 2 }}>
            Close
          </Button>
        </DialogContent>
      </Dialog>
    </Container>
  );
}
